import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn, setUpTOTP, verifyTOTPSetup, fetchUserAttributes, updateUserAttributes, updateMFAPreference } from 'aws-amplify/auth';
import StepWizard from 'react-step-wizard';
import { TOTP } from 'otpauth';
import QR from 'qrcode';
import { useCognitoAuthContext } from '@caplend/cognito-auth';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import { LoginProvider, useLoginContext } from './LoginContext';

function LoginProviderWrapper({ initialEmail = '', initialError }) {
  return (
    <LoginProvider initialEmail={initialEmail} initialError={initialError}>
      <Login />
    </LoginProvider>
  );
}

LoginProviderWrapper.propTypes = {
  initialEmail: PropTypes.string,
  initialError: PropTypes.string,
};

function Login() {
  const navigate = useNavigate();
  const stepWizardRef = useRef();

  const { signInStep, setSignInStep, email, setEmail, password, setQrCodeImage, setSecretCode, userCode, setUserCode, setError } =
    useLoginContext();
  const { hasAccessToken, setHasAccessToken } = useCognitoAuthContext();

  const [redirect, setRedirect] = useState(false);
  const hasRequireMfaRef = useRef(false);

  useEffect(() => {
    if (redirect && hasAccessToken) {
      navigate('/');
      setRedirect(false);
    }
  }, [redirect, hasAccessToken, navigate]);

  const resetToLogin = (message) => {
    setSignInStep(null);
    setUserCode('');
    stepWizardRef.current?.goToStep?.(1);
    setError(message || 'Please sign in again.');
  };

  const isLostSignInSessionError = (err) => {
    const msg = String(err?.message || err?.name || '').toLowerCase();
    return msg.includes('signIn was not called'.toLowerCase()) || msg.includes('session has expired') || msg.includes('no sign in');
  };

  const handleMfaSetupComplete = async () => {
    try {
      await updateMFAPreference({ totp: 'PREFERRED' });
      if (hasRequireMfaRef.current) {
        await updateUserAttributes({
          userAttributes: {
            'custom:require_mfa': 'false',
          },
        });
        hasRequireMfaRef.current = false;
      }
    } catch (err) {
      setError(err.message);
      return;
    }
    setRedirect(true);
    setHasAccessToken(true);
  };

  const authenticateSubmit = async (e) => {
    e.preventDefault();
    setError();

    try {
      if (signInStep === 'TOTP_SETUP') {
        if (hasRequireMfaRef.current) {
          await verifyTOTPSetup({ code: userCode });
        } else {
          await confirmSignIn({ challengeResponse: userCode });
        }
        await handleMfaSetupComplete();
        return;
      }

      if (signInStep === 'MFA_REQUIRED') {
        await confirmSignIn({ challengeResponse: userCode });
        setHasAccessToken(true);
        setRedirect(true);
        return;
      }

      const normalizedEmail = email.trim().toLowerCase();
      setEmail(normalizedEmail);
      const result = await signIn({ username: normalizedEmail, password });

      if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        const { sharedSecret } = await setUpTOTP();
        const totp = new TOTP({
          issuer: 'Atrya-FX',
          label: normalizedEmail,
          algorithm: 'SHA1',
          digits: 6,
          period: 30,
          secret: sharedSecret,
        });
        const qrImage = await QR.toDataURL(totp.toString());
        setSecretCode(sharedSecret);
        setQrCodeImage(qrImage);
        setSignInStep('TOTP_SETUP');
        stepWizardRef.current.goToStep(2);
        return;
      }

      const nextSignInStep = result.nextStep?.signInStep;
      const isSoftwareTokenMfa =
        nextSignInStep === 'CONFIRM_SIGN_IN_WITH_SOFTWARE_TOKEN_MFA' ||
        nextSignInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
        nextSignInStep === 'CONFIRM_SIGN_IN_WITH_TOTP' ||
        nextSignInStep === 'SOFTWARE_TOKEN_MFA';

      if (isSoftwareTokenMfa) {
        setSignInStep('MFA_REQUIRED');
        setTimeout(() => stepWizardRef.current?.goToStep?.(3), 0);
        return;
      }

      if (result.isSignedIn) {
        const attributes = await fetchUserAttributes();
        const requireMfa = attributes['custom:require_mfa'] === 'true';
        if (requireMfa) {
          hasRequireMfaRef.current = true;
          const { sharedSecret } = await setUpTOTP();
          const totp = new TOTP({
            issuer: 'Atrya-FX',
            label: normalizedEmail,
            algorithm: 'SHA1',
            digits: 6,
            period: 30,
            secret: sharedSecret,
          });
          const qrImage = await QR.toDataURL(totp.toString());
          setSecretCode(sharedSecret);
          setQrCodeImage(qrImage);
          setSignInStep('TOTP_SETUP');
          stepWizardRef.current.goToStep(2);
          return;
        }
        setHasAccessToken(true);
        setRedirect(true);
      }
    } catch (err) {
      if (isLostSignInSessionError(err)) {
        resetToLogin('Your sign-in session expired. Please sign in again.');
        return;
      }
      setError(err.message || err.name || 'Sign in failed');
    }
  };

  return (
    <StepWizard ref={stepWizardRef} className='overflow-hidden'>
      <Step1 authenticateSubmit={authenticateSubmit} />
      <Step2 authenticateSubmit={authenticateSubmit} />
      <Step3 authenticateSubmit={authenticateSubmit} />
    </StepWizard>
  );
}

export default LoginProviderWrapper;
